import { supabase } from '../client';
import { B2B_CANDIDATE_RESUME_BUCKET } from '../storageConfig';
import { Organization, OrgRole, OrgUser } from '@core/types';

export const orgService = {
  /**
   * Return ALL orgs this Keycloak user belongs to, ordered by join date (oldest first).
   * Ordered oldest-first so the user's primary/original workspace is always index 0.
   */
  async listUserOrgs(
    keycloakSub: string,
  ): Promise<Array<{ org: Organization; role: OrgRole; orgUserId: string }>> {
    const { data: rows } = await supabase
      .from('ggj_b2b_org_users')
      .select('id, org_id, role')
      .eq('keycloak_sub', keycloakSub)
      .eq('is_active', true)
      .order('joined_at', { ascending: true }); // oldest first = primary org at [0]

    if (!rows?.length) return [];

    const orgIds = rows.map((r) => r.org_id as string);
    const { data: orgs } = await supabase
      .from('ggj_b2b_organizations')
      .select('*')
      .in('id', orgIds);

    return rows
      .map((r) => {
        const org = orgs?.find((o: Organization) => o.id === r.org_id);
        if (!org) return null;
        return { org: org as Organization, role: r.role as OrgRole, orgUserId: r.id as string };
      })
      .filter(Boolean) as Array<{ org: Organization; role: OrgRole; orgUserId: string }>;
  },

  /** Look up which org a Keycloak user belongs to (used at login time).
   *  Always picks the user's OLDEST (primary) org so accidental duplicate org
   *  creation during onboarding never shadows real data. */
  async getOrgByKeycloakSub(
    keycloakSub: string,
  ): Promise<{ org: Organization; role: OrgRole; orgUserId: string } | null> {
    const allOrgs = await orgService.listUserOrgs(keycloakSub);
    // [0] is the oldest (primary) org — listUserOrgs orders ascending by joined_at
    return allOrgs[0] ?? null;
  },

  async getOrg(orgId: string): Promise<Organization | null> {
    const { data } = await supabase
      .from('ggj_b2b_organizations')
      .select('*')
      .eq('id', orgId)
      .maybeSingle();
    return data;
  },

  /**
   * Phase 8 · C-37 / C-38 — Return the org's default TAT setting (days).
   *
   * Landed for real with migration 105 (cluster C-38). Before that migration
   * runs the column doesn't exist and PostgREST returns an error; we catch
   * it silently and fall back to 30 so the wizard can compute a reasonable
   * default without blocking on the migration landing.
   */
  async getDefaultTatDays(orgId: string): Promise<number> {
    try {
      const { data } = await supabase
        .from('ggj_b2b_organizations')
        .select('default_tat_days')
        .eq('id', orgId)
        .maybeSingle();
      const days = (data as Record<string, unknown> | null)?.default_tat_days;
      return typeof days === 'number' && days > 0 ? days : 30;
    } catch {
      return 30;
    }
  },

  /**
   * Phase 8 · C-38 — Persist the org's default TAT setting.
   *
   * Validates the 1–365 range up-front so the UI never round-trips a bad
   * value (the DB CHECK constraint also enforces the same bounds). The
   * caller is responsible for invalidating any cached reads (the
   * AutomationSettings UI invalidates the `['org','defaults',orgId]` key).
   */
  async setDefaultTatDays(orgId: string, days: number): Promise<void> {
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      throw new Error('TAT must be between 1 and 365 days.');
    }
    const { error } = await supabase
      .from('ggj_b2b_organizations')
      .update({ default_tat_days: days })
      .eq('id', orgId);
    if (error) throw error;
  },

  async createOrg(payload: {
    id: string;
    name: string;
    slug: string;
    billing_email?: string;
    gst_number?: string;
    /** §9.1 — set from onboarding legal step when columns exist (migration 023). */
    gdpr_dpa_signed?: boolean;
    gdpr_dpa_signed_at?: string | null;
    consent_version?: string | null;
  }): Promise<Organization> {
    const { data, error } = await supabase
      .from('ggj_b2b_organizations')
      .insert({
        id: payload.id,
        name: payload.name,
        slug: payload.slug,
        billing_email: payload.billing_email ?? null,
        gst_number: payload.gst_number ?? null,
        gdpr_dpa_signed: payload.gdpr_dpa_signed ?? false,
        gdpr_dpa_signed_at: payload.gdpr_dpa_signed_at ?? null,
        consent_version: payload.consent_version ?? null,
        plan: 'starter',
        seats_allocated: 1,
        credits_balance: 60,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateOrg(orgId: string, payload: Partial<Organization>): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_organizations')
      .update(payload)
      .eq('id', orgId);
    if (error) throw error;
  },

  async getTeamMembers(orgId: string): Promise<OrgUser[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_org_users')
      .select('*')
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order('joined_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async upsertOrgUser(payload: Partial<OrgUser> & { org_id: string; keycloak_sub: string }): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_org_users')
      .upsert(payload, { onConflict: 'org_id,keycloak_sub' });
    if (error) throw error;
  },

  /**
   * Per-member pipeline stats.
   * Returns two maps:
   *   byUserId[ggj_b2b_org_users.id]  → { applications, placed }
   *   byKeycloakSub[keycloak_sub] → { candidates }
   */
  async getTeamStats(orgId: string): Promise<{
    byUserId: Record<string, { applications: number; placed: number }>;
    byKeycloakSub: Record<string, { candidates: number }>;
  }> {
    const [appRes, placedRes, candRes] = await Promise.all([
      supabase.from('ggj_b2b_applications').select('assigned_to').eq('org_id', orgId).not('assigned_to', 'is', null),
      supabase.from('ggj_b2b_applications').select('assigned_to').eq('org_id', orgId).eq('status', 'placed').not('assigned_to', 'is', null),
      supabase.from('ggj_b2b_candidates').select('created_by').eq('org_id', orgId),
    ]);

    const byUserId: Record<string, { applications: number; placed: number }> = {};
    for (const row of appRes.data ?? []) {
      if (!row.assigned_to) continue;
      byUserId[row.assigned_to] ??= { applications: 0, placed: 0 };
      byUserId[row.assigned_to].applications++;
    }
    for (const row of placedRes.data ?? []) {
      if (!row.assigned_to) continue;
      byUserId[row.assigned_to] ??= { applications: 0, placed: 0 };
      byUserId[row.assigned_to].placed++;
    }

    const byKeycloakSub: Record<string, { candidates: number }> = {};
    for (const row of candRes.data ?? []) {
      if (!row.created_by) continue;
      byKeycloakSub[row.created_by] ??= { candidates: 0 };
      byKeycloakSub[row.created_by].candidates++;
    }

    return { byUserId, byKeycloakSub };
  },

  async deactivateMember(orgId: string, keycloakSub: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_org_users')
      .update({ is_active: false })
      .eq('org_id', orgId)
      .eq('keycloak_sub', keycloakSub);
    if (error) throw error;
  },

  /** ORG-020: Directly create an org_user row (dev/admin invite flow). */
  async addMember(orgId: string, params: {
    full_name: string;
    email: string;
    role: string;
    keycloak_sub?: string;
  }) {
    const keycloakSub = params.keycloak_sub || `pending:${crypto.randomUUID()}`;
    const { data, error } = await supabase
      .from('ggj_b2b_org_users')
      .insert({
        org_id: orgId,
        keycloak_sub: keycloakSub,
        full_name: params.full_name,
        email: params.email,
        role: params.role,
        is_active: true,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateMemberRole(orgId: string, memberId: string, role: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_org_users')
      .update({ role })
      .eq('org_id', orgId)
      .eq('id', memberId);
    if (error) throw error;
  },

  /**
   * Legacy helper — returns `/object/public/...` which only works for **public** buckets.
   * `ggj_b2b-candidate-resumes` is private; use `PrivateStorageAvatar` + signed URLs in the UI.
   */
  getMemberAvatarPublicUrl(storagePath: string): string {
    const { data } = supabase.storage.from(B2B_CANDIDATE_RESUME_BUCKET).getPublicUrl(storagePath);
    return data.publicUrl;
  },

  /** Upload profile image for the signed-in org member row; updates `avatar_url`. */
  async uploadMemberAvatar(orgId: string, orgUserId: string, file: File): Promise<string> {
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'jpg';
    const safeExt = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? ext : 'jpg';
    const path = `org-user-avatars/${orgId}/${orgUserId}/profile.${safeExt}`;
    const { error } = await supabase.storage
      .from(B2B_CANDIDATE_RESUME_BUCKET)
      .upload(path, file, { upsert: true });
    if (error) throw error;
    const { error: uerr } = await supabase
      .from('ggj_b2b_org_users')
      .update({ avatar_url: path })
      .eq('id', orgUserId)
      .eq('org_id', orgId);
    if (uerr) throw uerr;
    return path;
  },

  async clearMemberAvatar(orgId: string, orgUserId: string): Promise<void> {
    const { data: row } = await supabase
      .from('ggj_b2b_org_users')
      .select('avatar_url')
      .eq('id', orgUserId)
      .eq('org_id', orgId)
      .maybeSingle();
    const path = (row as { avatar_url?: string | null } | null)?.avatar_url;
    if (path) {
      await supabase.storage.from(B2B_CANDIDATE_RESUME_BUCKET).remove([path]);
    }
    const { error } = await supabase
      .from('ggj_b2b_org_users')
      .update({ avatar_url: null })
      .eq('id', orgUserId)
      .eq('org_id', orgId);
    if (error) throw error;
  },

  /** Same caveat as {@link getMemberAvatarPublicUrl} — private bucket needs signed URLs for GET. */
  getMemberResumePublicUrl(storagePath: string): string {
    const { data } = supabase.storage.from(B2B_CANDIDATE_RESUME_BUCKET).getPublicUrl(storagePath);
    return data.publicUrl;
  },

  /** Upload résumé for the signed-in org member; updates `resume_url`. */
  async uploadMemberResume(orgId: string, orgUserId: string, file: File): Promise<string> {
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'pdf';
    const safeExt = ['pdf', 'doc', 'docx'].includes(ext) ? ext : 'pdf';
    const path = `org-user-resumes/${orgId}/${orgUserId}/resume.${safeExt}`;
    const { error } = await supabase.storage
      .from(B2B_CANDIDATE_RESUME_BUCKET)
      .upload(path, file, { upsert: true });
    if (error) throw error;
    const { error: uerr } = await supabase
      .from('ggj_b2b_org_users')
      .update({ resume_url: path })
      .eq('id', orgUserId)
      .eq('org_id', orgId);
    if (uerr) throw uerr;
    return path;
  },

  async clearMemberResume(orgId: string, orgUserId: string): Promise<void> {
    const { data: row } = await supabase
      .from('ggj_b2b_org_users')
      .select('resume_url')
      .eq('id', orgUserId)
      .eq('org_id', orgId)
      .maybeSingle();
    const path = (row as { resume_url?: string | null } | null)?.resume_url;
    if (path) {
      await supabase.storage.from(B2B_CANDIDATE_RESUME_BUCKET).remove([path]);
    }
    const { error } = await supabase
      .from('ggj_b2b_org_users')
      .update({ resume_url: null })
      .eq('id', orgUserId)
      .eq('org_id', orgId);
    if (error) throw error;
  },
};
